import {
  EMPTY_STRING,
  globalConstants,
  inventoryConstants,
  printerConstants,
} from "../../../constants";
import {
  PRINT_LABEL_DESIGN_ENUMS,
  PRODUCT_CONDITIONS_ENUMS,
  PRODUCT_TYPES_ENUMS,
} from "../../../system/globalEnums";
import { getFormattedCondition, getShortestSku } from "../../utility";

const getPriceOffset = (itemPrice) => {
  let priceXaxisOffset;

  switch (itemPrice.length) {
    case 3:
      priceXaxisOffset = 315;
      break;
    case 4:
      priceXaxisOffset = 300;
    case 5:
      priceXaxisOffset = 305;
      break;
    case 6:
      priceXaxisOffset = 290;
      break;
    case 7:
      priceXaxisOffset = 275;
      break;
    case 8:
      priceXaxisOffset = 255;
      break;
    case 9:
      priceXaxisOffset = 240;
      break;
    case 10:
      priceXaxisOffset = 225;
      break;
    default:
      priceXaxisOffset = 250;
  }
  return priceXaxisOffset;
};

const truncateItemName = (product_name, maxLength = 25) => {
  // Split the product_name into words
  const words = product_name.split(" ");

  let firstLine = "";
  let currentLength = 0;

  for (let i = 0; i < words.length; i++) {
    const potentialLength =
      currentLength === 0
        ? words[i].length
        : currentLength + 1 + words[i].length;

    if (potentialLength > maxLength) {
      break;
    }

    if (currentLength === 0) {
      firstLine += words[i];
    } else {
      firstLine += " " + words[i];
    }

    currentLength = potentialLength;
  }

  let secondPart = product_name.slice(firstLine.length).trim();

  if (secondPart.length > maxLength) {
    secondPart = secondPart.slice(0, maxLength - 3) + "...";
  }

  return {
    firstPart: firstLine,
    secondPart: secondPart,
  };
};

//-------function to break string when reach max length by word not character
const sliceStringWithWordBreaks = (text, maxLength) => {
  const slicedStrings = [];
  let currentIndex = 0;

  while (currentIndex < text.length) {
    let endIndex = currentIndex + maxLength;

    if (endIndex > text.length) {
      endIndex = text.length;
    } else {
      while (endIndex > currentIndex && !/\s/.test(text[endIndex - 1])) {
        endIndex--;
      }
    }

    slicedStrings.push(text.slice(currentIndex, endIndex).trim());
    currentIndex = endIndex;
  }

  return slicedStrings;
};
const calculateOffsetofItemName = (
  str,
  minLength,
  maxLength,
  minOffset,
  maxOffset
) => {
  const length = str.length;

  if (length < minLength || length > maxLength) {
    return 0;
  }

  const offset =
    minOffset +
    ((maxOffset - minOffset) * (maxLength - length)) / (maxLength - minLength);

  return offset;
};

export const printZebraLabel = (
  type,
  selectedRowsForLabel,
  storeLogo,
  dpi = printerConstants.DEFAULT_DPI
) => {
  let zpl;
  const isNewItem =
    selectedRowsForLabel.productType === PRODUCT_TYPES_ENUMS.VIDEO_GAME
      ? selectedRowsForLabel.price.type === PRODUCT_CONDITIONS_ENUMS.NEW
        ? printerConstants.NEW_PRODUCT_LABEL
        : printerConstants.PRE_OWNED_PRODUCT_LABEL
      : EMPTY_STRING;

  //------- General Properties for label
  const itemTypeCondition = `${getFormattedCondition(selectedRowsForLabel)}`;
  const tagList =
    selectedRowsForLabel.tags?.filter(
      (tag) => tag !== inventoryConstants.CUSTOM_ITEM_TAG
    ) || [];

  //------- Standard Label Settings
  const standardLabelItemName = truncateItemName(
    selectedRowsForLabel.product_name,
    30
  );

  //------- Minimal Label Settings
  const itemName = truncateItemName(selectedRowsForLabel.product_name);
  const itemCategory =
    selectedRowsForLabel.category_name.length > 30
      ? `${selectedRowsForLabel.category_name.slice(0, 30)}...`
      : selectedRowsForLabel.category_name;
  const itemPrice = `${Number(
    selectedRowsForLabel.price.unit_sell_price
  ).toFixed(2)}`;
  const itemSku = getShortestSku(selectedRowsForLabel.sku);
  const itemCondition =
    itemTypeCondition.length > 30
      ? `${itemTypeCondition.slice(0, 30)}...`
      : itemTypeCondition;
  const itemTags =
    tagList?.join(", ").length > 30
      ? `${tagList.join(", ").replaceAll("#", "").slice(0, 30)}...`
      : tagList.join(", ").replaceAll("#", "");

  //------- Alternate Label Settings
  const spineLabelItemName = truncateItemName(
    selectedRowsForLabel.product_name,
    19
  );
  const splineLabelitemCategory =
    selectedRowsForLabel.category_name.length > 21
      ? `${selectedRowsForLabel.category_name.slice(0, 21)}...`
      : selectedRowsForLabel.category_name;
  const splineLabelItemCondition =
    itemTypeCondition.length > 19
      ? `${itemTypeCondition.slice(0, 19)}...`
      : itemTypeCondition;

  const priceXaxisOffset = getPriceOffset(itemPrice);

  //use this scale to scale the printing according to different printer DPI values, as DPI increases we need more dots to cover same
  // area so in order to have consistent size of printing across all printers we add scaling
  const scale = dpi / printerConstants.DEFAULT_DPI;
  // ZPL script to print a simple barcode
  if (type === PRINT_LABEL_DESIGN_ENUMS.MINIMAL.value) {
    zpl = [
      `^XA\n`,
      `^FWN\n`,
      `^BY${Math.ceil(1 * scale)},${Math.ceil(1 * scale)},${Math.round(
        60 * scale
      )}\n`,
      `^CF0,${Math.round(20 * scale)}\n`,
      `^FO${Math.round(30 * scale)},${Math.round(107 * scale)}${
        storeLogo || EMPTY_STRING
      }\n`,
      `^FO${Math.round(15 * scale)},${Math.round(15 * scale)}^FD${
        itemName.firstPart
      }^FS\n`,
      `^FO${Math.round(15 * scale)},${Math.round(40 * scale)}^FD${
        itemName.secondPart
      }^FS\n`,
      `^CF0,${Math.round(15 * scale)}\n`,
      `^FO${Math.round(15 * scale)},${
        itemName.secondPart ? Math.round(65 * scale) : Math.round(40 * scale)
      }^FD${itemCategory}^FS\n`,
      `^FO${Math.round(15 * scale)},${
        itemName.secondPart ? Math.round(80 * scale) : Math.round(60 * scale)
      }^FDCondition: ${itemCondition} ^FS\n`,
      `^FO${Math.round(15 * scale)},${
        itemName.secondPart ? Math.round(95 * scale) : Math.round(80 * scale)
      }^FD${itemTags}^FS\n`,
      `^CF0,${Math.round(35 * scale)}\n`,
      `^FO${Math.round(priceXaxisOffset * scale)},${Math.round(
        15 * scale
      )}^FD$${itemPrice}^FS\n`,
      `^FO${Math.round(
        calculateOffsetofItemName(
          itemSku,
          6,
          15,
          storeLogo ? 190 : 130,
          storeLogo ? 240 : 150
        ) * scale
      )},${Math.round(125 * scale)}^BC^FD${itemSku}^FS\n`,
      `^XZ\n`,
    ];
  } else if (type === PRINT_LABEL_DESIGN_ENUMS.ALTERNATE.value) {
    zpl = [
      `^XA\n`,
      `^FWR\n`,
      `^BY${Math.ceil(1 * scale)},${Math.ceil(1 * scale)},${Math.round(
        60 * scale
      )}\n`,
      `^CF0,${Math.round(20 * scale)}\n`,
      `^FO${Math.round(30 * scale)},${Math.round(107 * scale)}\n`,
      `^FO${Math.round(210 * scale)},${Math.round(
        calculateOffsetofItemName(
          spineLabelItemName.firstPart,
          1,
          19,
          20,
          100
        ) * scale
      )}^FD${spineLabelItemName.firstPart}^FS\n`,
      spineLabelItemName.secondPart
        ? `^FO${Math.round(190 * scale)},${
            spineLabelItemName.secondPart
              ? Math.round(
                  calculateOffsetofItemName(
                    spineLabelItemName.secondPart,
                    1,
                    21,
                    20,
                    100
                  ) * scale
                )
              : ""
          }^FD${spineLabelItemName.secondPart}^FS\n`
        : "",
      `^CF0,${Math.round(17 * scale)}\n`,
      `^FO${
        spineLabelItemName.secondPart
          ? Math.round(165 * scale)
          : Math.round(185 * scale)
      },${Math.round(
        calculateOffsetofItemName(splineLabelitemCategory, 1, 19, 20, 100) *
          scale
      )}^FD${splineLabelitemCategory}^FS\n`,
      `^FO${
        spineLabelItemName.secondPart
          ? Math.round(140 * scale)
          : Math.round(165 * scale)
      },${Math.round(
        calculateOffsetofItemName(splineLabelItemCondition, 1, 19, 20, 100) *
          scale
      )}^FD${splineLabelItemCondition} ^FS^FO${Math.round(
        15 * scale
      )},${Math.round(80 * scale)}^FD^FS\n`,
      `^CF0,${Math.round(35 * scale)}\n`,
      `^FO${Math.round(50 * scale)},${Math.round(
        calculateOffsetofItemName(itemPrice, 1, 10, 15, 90) * scale
      )}^FD$${itemPrice}^FS\n`,
      `^CF0,${Math.round(10 * scale)}\n`,
      `^FO${Math.round(
        110 * scale
      )},0^FD_________________________________________^FS\n`,
      `^FO${Math.round(270 * scale)},${Math.round(
        calculateOffsetofItemName(itemSku, 6, 15, 15, 70) * scale
      )}\n`,
      `^BCN\n`,
      `^FD${itemSku}^FS\n`,
      `^XZ\n`,
    ];
  } else if (type === PRINT_LABEL_DESIGN_ENUMS.STANDARD.value) {
    zpl = [
      `^XA\n`,
      `^FWN\n`,
      `^BY${Math.ceil(1 * scale)},${Math.round(1 * scale)},${Math.round(
        60 * scale
      )}\n`,
      `^CF0,${Math.round(15 * scale)}\n`,
      `^FO${Math.round(10 * scale)},${Math.round(15 * scale)}${
        storeLogo || EMPTY_STRING
      }\n`,
      `^FO${Math.round(15 * scale)},${Math.round(105 * scale)}^FD${
        standardLabelItemName.firstPart
      }^FS\n`,
      `^FO${Math.round(15 * scale)},${Math.round(125 * scale)}^FD${
        standardLabelItemName.secondPart
      }^FS\n`,
      `^CF0,${Math.round(15 * scale)}\n`,
      `^FO${Math.round(15 * scale)},${
        standardLabelItemName.secondPart
          ? Math.round(145 * scale)
          : Math.round(125 * scale)
      }^FD${itemCategory}^FS\n`,
      `^FO${Math.round(15 * scale)},${
        standardLabelItemName.secondPart
          ? Math.round(165 * scale)
          : Math.round(145 * scale)
      }^FDCondition: ${itemCondition} ^FS\n`,
      `^FO${Math.round(15 * scale)},${
        standardLabelItemName.secondPart
          ? Math.round(185 * scale)
          : Math.round(165 * scale)
      }^FD${itemTags}^FS\n`,
      `^CF0,${Math.round(30 * scale)}\n`,
      `^FO${
        isNewItem === printerConstants.NEW_PRODUCT_LABEL
          ? Math.round(270 * scale)
          : Math.round(200 * scale)
      },${Math.round(90 * scale)}^FD${isNewItem} ^FS\n`,
      `^CF0,${Math.round(60 * scale)}\n`,
      `^FO${Math.round(
        calculateOffsetofItemName(
          itemPrice,
          4,
          10,
          storeLogo ? 110 : 10,
          storeLogo ? 250 : 140
        ) * scale
      )},${Math.round(20 * scale)}^FD$${itemPrice}^FS\n`,
      `^FO${Math.round(260 * scale)},${Math.round(
        125 * scale
      )}^BC^FD${itemSku}^FS\n`,
      `^XZ\n`,
    ];
  }
  console.log("zpl", zpl);
  return zpl;
};

// export const printLabel = async (zpl) => {
//   try {
//     // Create a new instance of the object
//     const browserPrint = new ZebraBrowserPrintWrapper();

//     // Select default printer
//     const defaultPrinter = await browserPrint.getDefaultPrinter();

//     // Set the printer
//     browserPrint.setPrinter(defaultPrinter);
//     browserPrint.print(zpl);
//   } catch (error) {
//     customToast(error, toastType.ERROR);
//   }
// };
